/**
 * Recorrido de bienvenida: presenta en cuatro pasos los tres entornos
 * principales de la app la primera vez que el usuario entra al menú principal.
 */
import { useState } from "react";
import { FileStack, FileSpreadsheet, FolderOpen, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface OnboardingTourProps {
  open: boolean;
  /** Se llama al terminar o saltar el recorrido. */
  onClose: () => void;
  /** Nombre del usuario para el saludo inicial. */
  name?: string;
  canAccessIssuedInvoices?: boolean;
  canAccessFinancing?: boolean;
}

type Step = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  bullets?: string[];
  note?: string;
};

const OnboardingTour = ({
  open,
  onClose,
  name,
  canAccessIssuedInvoices = true,
  canAccessFinancing = true,
}: OnboardingTourProps) => {
  const [index, setIndex] = useState(0);

  const steps: Step[] = [
    {
      id: "welcome",
      icon: Sparkles,
      title: `Te damos la bienvenida${name ? `, ${name}` : ""}`,
      description:
        "Esta plataforma automatiza la contabilización de facturas: lee los documentos, propone los datos contables y genera el fichero listo para A3.",
      bullets: [
        "En un minuto te enseñamos dónde está cada cosa.",
        "Puedes saltar el recorrido y repetirlo cuando quieras.",
      ],
    },
    {
      id: "recibidas",
      icon: FileStack,
      title: "Procesador de recibidas",
      description:
        "Sube las facturas de proveedores en PDF o Excel, revísalas en pantalla y genera el fichero SUENLACE para traspasar a A3.",
      bullets: [
        "Arrastra los documentos y espera a que se extraigan los datos.",
        "Corrige lo que haga falta en la vista dividida documento / datos.",
        "Exporta el fichero y el ZIP con los documentos renombrados.",
      ],
    },
    {
      id: "emitidas",
      icon: FileSpreadsheet,
      title: "Procesador de emitidas",
      description:
        "Lo mismo con las facturas de clientes: desde PDF o desde el Excel de facturación del cliente, con plantillas por programa de facturación.",
      bullets: [
        "Elige primero la empresa y después la plantilla.",
        "Revisa cuentas de cliente y contrapartidas antes de exportar.",
      ],
      note: canAccessIssuedInvoices
        ? undefined
        : "Ahora mismo no tienes este entorno habilitado: pídeselo al administrador.",
    },
    {
      id: "datos",
      icon: FolderOpen,
      title: "Datos de empresa",
      description:
        "Consulta el histórico de facturas ya tramitadas, los maestros de proveedores y clientes, y los cuadros de financiación.",
      bullets: [
        "Histórico: busca y descarga documentos tramitados.",
        "Maestros: cuentas contables por empresa.",
        "Financiación: cuadros de leasings y préstamos.",
      ],
      note: canAccessFinancing
        ? undefined
        : "Financiación requiere permiso: pídeselo al administrador si lo necesitas.",
    },
  ];

  const step = steps[index];
  const Icon = step.icon;
  const isLast = index === steps.length - 1;

  const finish = () => {
    setIndex(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand mb-2">
            <Icon className="h-6 w-6" />
          </div>
          <DialogTitle className="text-2xl font-semibold font-[Playfair_Display,serif] text-left">
            {step.title}
          </DialogTitle>
          <DialogDescription className="text-left text-base">
            {step.description}
          </DialogDescription>
        </DialogHeader>

        {step.bullets && (
          <ul className="space-y-2 text-sm text-muted-foreground">
            {step.bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {step.note && (
          <p className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
            {step.note}
          </p>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === index ? "w-5 bg-brand" : "w-1.5 bg-muted-foreground/30",
                )}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {index > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setIndex((i) => i - 1)}>
                Atrás
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={finish}>
                Saltar
              </Button>
            )}
            <Button
              size="sm"
              className="bg-brand hover:bg-brand/90 text-brand-foreground"
              onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
            >
              {isLast ? "Empezar" : "Siguiente"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTour;
